import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Barcode, ClipboardText, Recycle, UsersThree, Package } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, localBusiness } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALT = {
  store: "A tidy factory parts store with labelled bins of bolts, washers and fasteners on steel shelving",
  scan: "A gloved hand scanning the barcode on a parts bin with a phone on the shop floor",
  count: "Two workers counting small parts back into a tray at the end of a shift",
  scrap: "A labelled scrap bin on a factory floor being photographed for the scrap record",
  crib: "A tool crib hatch with rolls of tape, gloves and spray cans on the shelves behind it",
  talk: "Factory workers at a shift briefing board on the shop floor",
};

const ICONS = { store: ShieldCheck, scan: Barcode, count: ClipboardText, scrap: Recycle, crib: Package, talk: UsersThree };

export default function PartsGuardPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/parts-guard/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ sections: [] }));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbs([["Home", "/"], ["Parts integrity", "/parts-guard"]]),
      localBusiness({
        name: "Unity & Utility parts integrity",
        description: "Assembly line parts tracking that stops parts walking home: every kit scanned out to a named operator at a named station, counted back in at shift end, shortages alerted to the supervisor with a reason and photo proof of returned scrap, and a repeat-shortage watchlist.",
        path: "/parts-guard",
        image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
        area: "Worldwide",
      }),
    ],
  };

  useSeo({
    title: "Stop parts walking off the assembly line — parts integrity tracking | Unity & Utility",
    description: "Operators taking parts home from the line stops when the count happens every shift. Scan each kit out to a named operator and station, count it back in at shift end, give a reason for anything missing, photograph returned scrap, and let the repeat-shortage watchlist do the rest. Works in any country and currency. From $25 a month.",
    path: "/parts-guard",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd,
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="guard-public-page">
        <Crumbs items={[["Home", "/"], ["Parts integrity", "/parts-guard"]]} />
        <p className="overline text-[#0B5C2E]">Parts integrity on the line</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <ShieldCheck size={34} weight="duotone" className="mt-1 shrink-0 text-[#0B5C2E]" />
          Parts stop walking when they are counted
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="guard-public-intro">
          Bolts, batteries, cable, tape, bits and blades go home in ones and twos, and nobody
          misses them until the stock count is wrong months later. This closes the gap in one
          move: every kit is scanned out to a named operator at a named station for a named
          shift, counted back in at the end of it, and anything that does not come back needs a
          reason — with a photograph of the scrap. Three unexplained shortages puts a name on the
          watchlist. Every operator signs the honesty policy, so nobody can say they did not
          know. Runs in any country, in that country's own currency. Pictures on this board are
          redrawn by AI every 24 hours.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/parts-guard`}
            text="Parts walking off the assembly line stop walking when every kit is scanned out to a name and counted back in at the end of the shift."
            testid="guard-public-share" instagram marketplace />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt={ALT.store} loading="lazy"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="guard-public-image" />
        )}

        <section className="mt-6 border border-[#0B5C2E] bg-[#F3FAF5] p-5" data-testid="guard-public-card">
          <p className="overline text-[#0B5C2E]">
            Today{board?.day ? ` · ${board.day}` : ""}
            {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Stations counted", board?.stations, "guard-stat-stations"],
              ["Kits counted back", board?.kits_counted, "guard-stat-kits"],
              ["Shortages, 30 days", board?.shortages_30d, "guard-stat-shortages"],
              ["Closed off", board?.closed_30d, "guard-stat-closed"]].map(([l, v, t]) => (
              <div key={t} className="border border-border bg-white p-3" data-testid={t}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">{v ?? 0}</p>
                <p className="text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-600">
            Nothing on this board names a plant, a line or a person. Reports that do name people
            are seen only by managers and the security team.
          </p>
        </section>

        {(board?.sections || []).map((s) => {
          const Icon = ICONS[s.key] || ShieldCheck;
          return (
            <section key={s.key} className="mt-10" data-testid={`guard-public-section-${s.key}`}>
              <h2 className="font-display flex items-center gap-2 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
                <Icon size={18} weight="duotone" className="text-[#0B5C2E]" /> {s.label}
              </h2>
              {board?.section_images?.[s.key] && (
                <img src={`${BACKEND}${board.section_images[s.key]}`} alt={ALT[s.key] || s.label}
                  loading="lazy" className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
                  data-testid={`guard-public-image-${s.key}`} />
              )}
              <div className="mt-3">
                <ShareBar url={`${window.location.origin}/parts-guard`}
                  text={`${s.label} — how an assembly line keeps its parts where they belong.`}
                  testid={`guard-public-share-${s.key}`} compact instagram />
              </div>
            </section>
          );
        })}

        {board?.talk_of_the_day && (
          <section className="mt-10 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-4" data-testid="guard-public-talk">
            <p className="overline text-[#002FA7]">Toolbox talk of the day</p>
            <h2 className="font-display mt-1 text-base font-bold text-[#0F172A] md:text-lg">
              {board.talk_of_the_day.title}
            </h2>
            <ul className="mt-2 space-y-1.5 text-base leading-relaxed text-slate-700">
              {(board.talk_of_the_day.points || []).map((p, i) => (
                <li key={i} data-testid={`guard-public-talk-point-${i}`}>{p}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-10" data-testid="guard-public-risk">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            The parts that actually go missing
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Small, valuable and useful at home — these are flagged high risk and counted tightest.
            Every other part is still counted, just without the extra checks.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(board?.high_risk_examples || []).map((p) => (
              <span key={p.name} className="border border-border bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600">
                {p.name}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-10" data-testid="guard-public-allowances">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Consumables, on a fair allowance
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Tape, gloves, blades, spray and batteries are not signed out in a kit, so they are
            drawn against a monthly allowance per person and declared for a job on the line.
            Over the allowance waits for a supervisor, and twice the allowance shows up as
            abnormal usage by name.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {(board?.consumable_allowances || []).map((c) => (
              <div key={c.name} className="border border-border bg-white p-3"
                data-testid={`guard-public-allowance-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{c.name}</p>
                <p className="text-xs text-slate-500">{c.per_month} a month per person</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10" data-testid="guard-public-plans">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            What it costs
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {Object.entries(board?.plans || {}).map(([key, plan]) => (
              <div key={key} className="border border-border bg-white p-4" data-testid={`guard-public-plan-${key}`}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">
                  ${plan.price}<span className="text-sm font-normal text-slate-500">/month</span>
                </p>
                <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{plan.label}</p>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {(plan.includes || []).map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {board?.trial_days ?? 14} days free on any plan, then pay monthly — or pay for{" "}
            {board?.yearly_months ?? 10} months and get the year. One shortage caught usually
            covers it.
          </p>
          <Link to="/parts-guard-desk" data-testid="guard-public-open-desk"
            className="tap-safe mt-3 inline-flex items-center text-sm font-semibold text-[#002FA7] underline">
            Open the parts integrity desk
          </Link>
        </section>

        <div className="mt-10">
          <NotifyMe source="parts-guard" testid="guard-public-notify"
            blurb="Tell us the plant and the country you are in and we will tell you when parts integrity tracking is set up for your line." />
        </div>
      </div>
    </PublicShell>
  );
}
